import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";

const togglePublished = async ({
	id,
	published
}: {
	id: number;
	published: boolean;
}) => {
	const response = await cmsClient.patch<IEvent>(
		`/homepage/admin/events/${id}/`,
		{ published }
	);

	return response.data;
};

const useToggleEventPublished = () => {
	const queryClient = useQueryClient();

	const onMutate = async (variables: { id: number; published: boolean }) => {
		await queryClient.cancelQueries(["events"]);

		const previous = queryClient.getQueryData<IEvent[]>(["events"]);

		queryClient.setQueryData<IEvent[]>(["events"], (old) =>
			(old ?? []).map((event) =>
				event.id === variables.id
					? { ...event, published: variables.published }
					: event
			)
		);

		return { previous };
	};

	const onSuccess = (_data: IEvent, variables: { published: boolean }) => {
		const message = variables.published
			? "Event is now live"
			: "Event moved to drafts";

		toast.success(<CustomToast type="Success" message={message} />, {
			hideProgressBar: true
		});
	};

	const onSettled = () => {
		queryClient.invalidateQueries(["events"]);
	};

	const onError = (
		_error: unknown,
		_variables: { id: number; published: boolean },
		context?: { previous?: IEvent[] }
	) => {
		if (context?.previous) {
			queryClient.setQueryData(["events"], context.previous);
		}

		toast.error(
			<CustomToast type="Error" message="Something went wrong, try again" />,
			{ hideProgressBar: true }
		);
	};

	const mutation = useMutation(togglePublished, {
		onMutate,
		onError,
		onSuccess,
		onSettled
	});

	return mutation;
};

export default useToggleEventPublished;
